import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { formatMinor } from "@convex/payments/domain";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { getStoredUserSession } from "@shared/hooks/useUserSession";
import { amountAriaLabel, formatPaidAt } from "../../lib/money";

export const Route = createFileRoute("/app/receipts/$receiptId")({
  component: ReceiptPage,
});

function ReceiptPage() {
  const { receiptId } = Route.useParams();
  const { userId, sessionToken } = getStoredUserSession();
  const receipt = useQuery(api.receipts.getUserReceipt, userId && sessionToken ? { userId, sessionToken, receiptId } : "skip");

  if (!userId || !sessionToken) {
    window.location.href = "/auth/login";
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-24 px-4 pt-4">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/app/receipts" className="p-2 rounded-lg bg-white shadow-sm" aria-label="Înapoi">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-xl font-bold text-text">Chitanță</h1>
      </div>

      {receipt === undefined && (
        <div className="w-10 h-10 mx-auto mt-10 animate-spin motion-reduce:animate-none rounded-full border-4 border-primary border-t-transparent" />
      )}
      {receipt === null && <p className="text-center text-text-muted mt-10">Chitanța nu există.</p>}

      {receipt && (
        <article className="bg-white rounded-2xl p-6 shadow-sm" data-testid="receipt">
          <div className="text-center mb-5">
            <CheckCircle size={40} weight="fill" className="mx-auto text-success mb-2" />
            <p className="text-3xl font-bold text-text" aria-label={amountAriaLabel(receipt.amountMinor)}>
              {formatMinor(receipt.amountMinor)}
            </p>
            <p className="text-sm text-text-muted mt-1">Plătit prin RoPay</p>
          </div>

          <dl className="divide-y divide-gray-100 text-sm">
            <Row label="Număr" value={receipt.receiptNumber} mono />
            <Row label="Data" value={formatPaidAt(receipt.paidAt)} />
            <Row label="Comerciant" value={receipt.merchantName} />
            {receipt.merchantLegalName && <Row label="Denumire legală" value={receipt.merchantLegalName} />}
            {receipt.merchantCui && <Row label="CUI" value={receipt.merchantCui} />}
            {receipt.merchantAddress && <Row label="Adresă" value={receipt.merchantAddress} />}
            <Row label="Locație" value={receipt.locationName} />
            {receipt.reference && <Row label="Referință" value={receipt.reference} />}
          </dl>
        </article>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 py-2.5">
      <dt className="text-text-muted">{label}</dt>
      <dd className={`text-text text-right ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}
